import type {
  IDailyHourlyReadingsParams,
  IDailyTemperaturePeriodFilter,
} from '@/interfaces/entities/daily-temperature'

const HOUR_PATTERN = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

export const PERIOD_SELECT_FULL_DAY = 'full_day'
export const PERIOD_SELECT_MANUAL = 'manual'
export const DEFAULT_DAILY_TEMPERATURE_PAGE_SIZE = 5

export interface IDailyTemperaturePaginationOptions {
  page?: number
  items?: number
  sensorIds?: number[]
}

export function normalizeSensorIdsForQuery(sensorIds?: number[]): number[] | undefined {
  if (!sensorIds?.length) return undefined
  const uniqueSorted = [...new Set(sensorIds.filter((id) => Number.isFinite(id)))].sort(
    (a, b) => a - b,
  )
  return uniqueSorted.length ? uniqueSorted : undefined
}

export function sensorIdsCacheSegment(sensorIds?: number[]): string {
  return normalizeSensorIdsForQuery(sensorIds)?.join(',') ?? ''
}

export function createDefaultPeriodFilter(): IDailyTemperaturePeriodFilter {
  return {
    mode: 'full_day',
    shiftId: null,
    startHour: '',
    endHour: '',
  }
}

export function isCustomHourEnabled(filter: IDailyTemperaturePeriodFilter): boolean {
  return filter.mode === 'manual'
}

export function periodSelectValue(filter: IDailyTemperaturePeriodFilter): string {
  if (filter.mode === 'manual') return PERIOD_SELECT_MANUAL
  if (filter.mode === 'shift' && filter.shiftId != null) return `shift:${filter.shiftId}`
  return PERIOD_SELECT_FULL_DAY
}

export function applyPeriodSelectValue(
  value: string,
  current: IDailyTemperaturePeriodFilter,
): IDailyTemperaturePeriodFilter {
  if (value === PERIOD_SELECT_FULL_DAY) {
    return createDefaultPeriodFilter()
  }

  if (value === PERIOD_SELECT_MANUAL) {
    return {
      mode: 'manual',
      shiftId: null,
      startHour: current.startHour,
      endHour: current.endHour,
    }
  }

  if (value.startsWith('shift:')) {
    const shiftId = Number(value.slice('shift:'.length))
    if (!Number.isNaN(shiftId)) {
      return {
        mode: 'shift',
        shiftId,
        startHour: '',
        endHour: '',
      }
    }
  }

  return createDefaultPeriodFilter()
}

export function resetShiftIfUnavailable(
  filter: IDailyTemperaturePeriodFilter,
  hasShifts: boolean,
): IDailyTemperaturePeriodFilter {
  if (!hasShifts && filter.mode === 'shift') {
    return createDefaultPeriodFilter()
  }
  return filter
}

export function buildReadingsQueryParams(
  date: string,
  filter: IDailyTemperaturePeriodFilter,
  options?: IDailyTemperaturePaginationOptions,
): IDailyHourlyReadingsParams {
  const params: IDailyHourlyReadingsParams = { date }

  const hasPagination = options != null && (options.page != null || options.items != null)
  if (hasPagination) {
    params.page = options.page ?? 1
    params.items = options.items ?? DEFAULT_DAILY_TEMPERATURE_PAGE_SIZE
  }

  const sensorIds = normalizeSensorIdsForQuery(options?.sensorIds)
  if (sensorIds) {
    params.sensor_ids = sensorIds
  }

  if (filter.mode === 'shift' && filter.shiftId != null) {
    params.shift_id = filter.shiftId
    return params
  }

  if (filter.mode === 'manual') {
    params.start_hour = filter.startHour
    params.end_hour = filter.endHour
  }

  return params
}

export function buildDailyTemperatureCacheKey(params: IDailyHourlyReadingsParams): string {
  return [
    params.date,
    params.shift_id ?? '',
    params.start_hour ?? '',
    params.end_hour ?? '',
    sensorIdsCacheSegment(params.sensor_ids),
    params.items ?? DEFAULT_DAILY_TEMPERATURE_PAGE_SIZE,
    params.page ?? 1,
  ].join('|')
}

export function buildDailyTemperatureCachePrefix(params: IDailyHourlyReadingsParams): string {
  return [
    params.date,
    params.shift_id ?? '',
    params.start_hour ?? '',
    params.end_hour ?? '',
    sensorIdsCacheSegment(params.sensor_ids),
    params.items ?? DEFAULT_DAILY_TEMPERATURE_PAGE_SIZE,
  ].join('|')
}

export function formatDisplayDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-')
  if (!year || !month || !day) return isoDate
  return `${day}/${month}/${year}`
}

export function formatShortDisplayDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-')
  if (!year || !month || !day) return isoDate
  return `${day}/${month}`
}

export function todayISO(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export type DailyTemperatureValidationErrorKey =
  | 'errors.compare_date_required'
  | 'errors.compare_date_same_as_primary'
  | 'errors.manual_incomplete'
  | 'errors.manual_same_hour'
  | 'errors.invalid_hour'
  | 'errors.invalid_date'

export function isCompareSearchActive(
  isCompareEnabled: boolean,
  compareDate: string,
): boolean {
  return isCompareEnabled && compareDate.trim().length > 0
}

export function validateSearchFilters(
  primaryDate: string,
  compareDate: string,
  isCompareEnabled: boolean,
  periodFilter: IDailyTemperaturePeriodFilter,
): DailyTemperatureValidationErrorKey | null {
  if (!primaryDate) return 'errors.invalid_date'

  const normalizedCompareDate = compareDate.trim()
  if (
    isCompareSearchActive(isCompareEnabled, normalizedCompareDate) &&
    normalizedCompareDate === primaryDate
  ) {
    return 'errors.compare_date_same_as_primary'
  }

  if (periodFilter.mode === 'manual') {
    if (!periodFilter.startHour || !periodFilter.endHour) {
      return 'errors.manual_incomplete'
    }
    if (!HOUR_PATTERN.test(periodFilter.startHour) || !HOUR_PATTERN.test(periodFilter.endHour)) {
      return 'errors.invalid_hour'
    }
    if (periodFilter.startHour === periodFilter.endHour) {
      return 'errors.manual_same_hour'
    }
  }

  return null
}

export function validatePdfFilters(
  date: string,
  periodFilter: IDailyTemperaturePeriodFilter,
): DailyTemperatureValidationErrorKey | null {
  if (!date) return 'errors.invalid_date'
  return validateSearchFilters(date, date, false, periodFilter)
}

export function hasSetpoint(sensor: { setpoint: number | null }): boolean {
  return sensor.setpoint !== null && sensor.setpoint !== undefined
}

export function fmtTemperature(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${value.toFixed(1).replace('.', ',')}°`
}

export function fmtSetpoint(setpoint: number | null): string {
  if (setpoint === null || setpoint === undefined) return ''
  return `${setpoint.toFixed(1).replace('.', ',')}°`
}

export function isOutsideSetpoint(
  sensor: { setpoint: number | null; values: Record<string, number | null> },
  hour: string,
): boolean {
  const value = sensor.values?.[hour]
  return hasSetpoint(sensor) && value !== null && value !== undefined && value > (sensor.setpoint as number)
}

export function cellClass(
  sensor: { setpoint: number | null; values: Record<string, number | null> },
  hour: string,
): string {
  const value = sensor.values?.[hour]
  const isEmpty = value === null || value === undefined
  if (isOutsideSetpoint(sensor, hour)) return 'bg-red-500 font-bold text-white'
  if (isEmpty) return 'text-gray-300'
  return 'text-slate-700'
}

export const FIELD_INPUT_CLASS =
  'w-full rounded-none border border-zinc-300 bg-white py-3 pl-4 pr-4 text-sm text-gray-900 transition-colors hover:border-zinc-400 focus:border-primary focus:outline-none'
